import React from 'react';

const DEFAULT_FALLBACK_MESSAGE = 'Сталася помилка. Спробуйте пізніше';

type UseFormErrorsOptions = {
  fallbackMessage?: string;
  /**
   * Reset the error summary whenever any value in this list changes by
   * `Object.is` comparison. Pass a stable-identity array — recreating the
   * array on every render still works (React diffs element-by-element)
   * but allocating new contents will trigger an unnecessary reset.
   */
  resetOn?: unknown[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

type ValidationErrorLike = {
  constraints?: Record<string, unknown>;
  children?: unknown[];
};

const extractValidationErrors = (value: unknown): string[] => {
  if (!isRecord(value)) {
    return [];
  }

  const errors: string[] = [];
  if (isRecord(value.constraints)) {
    Object.values(value.constraints).forEach(message => {
      if (message) {
        errors.push(String(message));
      }
    });
  }

  if (Array.isArray(value.children)) {
    value.children.forEach(child => {
      errors.push(...extractValidationErrors(child));
    });
  }

  return errors;
};

const isValidationErrorLike = (value: unknown): value is ValidationErrorLike =>
  isRecord(value) &&
  (isRecord(value.constraints) || Array.isArray(value.children));

const extractStringErrors = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return [value];
  }

  if (typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => extractStringErrors(item));
  }

  if (isRecord(value)) {
    if (isValidationErrorLike(value)) {
      return extractValidationErrors(value);
    }
    return Object.values(value).flatMap(item => extractStringErrors(item));
  }

  return [];
};

const getErrorData = (err: unknown): unknown => {
  if (!isRecord(err)) {
    return null;
  }

  const response = isRecord(err.response) ? err.response : null;
  if (!response || !('data' in response)) {
    return null;
  }

  return response.data;
};

/**
 * Builds a list of error messages from an API error response.
 * @param err
 * @param fallbackMessage
 */
export function getApiErrorSummary(
  err: unknown,
  fallbackMessage: string = DEFAULT_FALLBACK_MESSAGE
): string[] {
  const data = getErrorData(err);
  if (!data) {
    return [fallbackMessage];
  }

  if (typeof data === 'string') {
    return [data];
  }

  if (Array.isArray(data)) {
    const arrayErrors = extractStringErrors(data);
    return arrayErrors.length > 0 ? arrayErrors : [fallbackMessage];
  }

  if (isRecord(data)) {
    const errors = extractStringErrors(data.errors);
    if (errors.length > 0) {
      return errors;
    }

    const messages = extractStringErrors(data.messages);
    if (messages.length > 0) {
      return messages;
    }

    if (typeof data.message === 'string') {
      return [data.message];
    }

    if (typeof data.error === 'string') {
      return [data.error];
    }
  }

  return [fallbackMessage];
}

/**
 * Manages API error summary state for modal forms.
 * @param options
 */
export function useFormErrors(options: UseFormErrorsOptions = {}) {
  const fallbackMessage = options.fallbackMessage ?? DEFAULT_FALLBACK_MESSAGE;
  const resetOn = options.resetOn ?? [];
  const [errorSummary, setErrorSummary] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (resetOn.length === 0) {
      return;
    }
    setErrorSummary([]);
  }, resetOn);

  const setApiError = React.useCallback(
    (err: unknown, overrideFallback?: string) => {
      const summary = getApiErrorSummary(
        err,
        overrideFallback ?? fallbackMessage
      );
      setErrorSummary(summary);
      return summary;
    },
    [fallbackMessage]
  );

  const clearErrors = React.useCallback(() => {
    setErrorSummary([]);
  }, []);

  return {
    errorSummary,
    errorMessage: errorSummary[0] || '',
    setApiError,
    clearErrors,
    setErrorSummary,
  };
}
